'use client'

import React from 'react'

import { Button } from '@/components/ui/button'
import { PayloadRichText } from '@/components/PayloadRichText'
import { Field, FieldGroup, FieldLabel, FieldSet } from '@/components/ui/field'
import { Input } from '@/components/ui/input'
import { Textarea } from '@/components/ui/textarea'
import { richTextToPlainText } from '@/blocks/block-utils'
import { cn } from '@/lib/utils'
import type { Form } from '@/payload-types'
import { getFormConfirmationMessage } from '@/utilities/formConfirmationMessage'
import {
  PHONE_PATTERN,
  validateFormFieldValues,
} from '@/utilities/formFieldValidation'

export type FormBuilderFieldLike = {
  blockType?: string
  defaultValue?: boolean | number | string
  /** Checkbox labels may be Lexical serialized state (`{ root }`) per Payload Forms. */
  label?: unknown
  message?: unknown
  name?: string
  options?: Array<{ label: string; value: string }>
  placeholder?: string | null
  required?: boolean
}

function resolveFormFieldLabel(label: unknown, fieldName: string): string {
  if (typeof label === 'string' && label.trim()) return label.trim()
  const plain = richTextToPlainText(label)?.trim()
  if (plain) return plain
  return fieldName
}

function getFieldPlaceholder(field: FormBuilderFieldLike): string | undefined {
  if (typeof field.placeholder !== 'string') return undefined
  const trimmed = field.placeholder.trim()
  return trimmed || undefined
}

function targetElementFromPointerEvent(eventTarget: EventTarget | null): Element | null {
  if (eventTarget instanceof Element) return eventTarget
  if (eventTarget instanceof Node) return eventTarget.parentElement
  return null
}

/** Allow link/button clicks without toggling the checkbox (same behavior as Contact section form). */
function isInteractiveCheckboxLabelClickTarget(target: EventTarget | null): boolean {
  const element = targetElementFromPointerEvent(target)
  return Boolean(element?.closest('a, button, input, textarea, select'))
}

function isLexicalCheckboxLabel(label: unknown): boolean {
  return Boolean(label && typeof label === 'object' && 'root' in label)
}

export type FormBuilderFormProps = {
  form: Form
  /** Prefix for stable input ids (`contact-*`, `map-contact-*`, …). */
  idPrefix: string
  /** Extra rows stored with the submission (e.g. map `locationId`). */
  extraSubmissionRows?: { field: string; value: boolean | number | string }[]
  className?: string
}

export function FormBuilderForm({
  extraSubmissionRows,
  form: formDoc,
  idPrefix,
  className,
}: FormBuilderFormProps) {
  const [isSubmitting, setIsSubmitting] = React.useState(false)
  const [statusMessage, setStatusMessage] = React.useState<string | null>(null)
  const [fieldErrors, setFieldErrors] = React.useState<Record<string, string>>({})
  const [values, setValues] = React.useState<Record<string, boolean | number | string>>({})

  const submitLabel = formDoc.submitButtonLabel || 'Send message'
  const confirmationMessage = getFormConfirmationMessage(formDoc)
  const supportedFields = (formDoc.fields ?? []) as FormBuilderFieldLike[]

  const onChangeValue = (fieldName: string, value: boolean | number | string) => {
    setValues((prev) => ({ ...prev, [fieldName]: value }))
    setFieldErrors((prev) => {
      if (!(fieldName in prev)) return prev
      const next = { ...prev }
      delete next[fieldName]
      return next
    })
  }

  const getInitialValue = (field: FormBuilderFieldLike): boolean | number | string => {
    switch (field.blockType) {
      case 'checkbox':
        return Boolean(field.defaultValue)
      case 'number':
      case 'payment':
        return typeof field.defaultValue === 'number' ? field.defaultValue : 0
      default:
        return field.defaultValue ?? ''
    }
  }

  const onSubmit = async (event: React.FormEvent<HTMLFormElement>) => {
    event.preventDefault()

    const errors = validateFormFieldValues(supportedFields, values, (field) =>
      field.name ? resolveFormFieldLabel(field.label, field.name) : 'This field',
    )
    setFieldErrors(errors)

    if (Object.keys(errors).length > 0) {
      setStatusMessage('Please correct the highlighted fields and try again.')
      return
    }

    setIsSubmitting(true)
    setStatusMessage(null)

    try {
      const submissionData = [
        ...supportedFields.map((field) => ({
          field: field.name ?? '',
          value: field.name ? (values[field.name] ?? getInitialValue(field)) : getInitialValue(field),
        })),
        ...(extraSubmissionRows ?? []).map((row) => ({
          field: row.field,
          value: row.value,
        })),
      ]

      const response = await fetch('/api/form-submissions', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          form: formDoc.id,
          submissionData,
        }),
      })

      if (!response.ok) {
        setStatusMessage('Could not submit form. Please try again.')
        return
      }

      setStatusMessage(confirmationMessage)
      setValues({})
      setFieldErrors({})
    } catch {
      setStatusMessage('Network error while submitting the form.')
    } finally {
      setIsSubmitting(false)
    }
  }

  return (
    <form aria-label="Contact form" className={className} onSubmit={onSubmit}>
      <FieldSet>
        <FieldGroup>
          {supportedFields.map((field) => {
            if (!field.name) return null

            const fieldName = field.name
            const fieldId = `${idPrefix}-${fieldName}`
            const fieldLabel = resolveFormFieldLabel(field.label, fieldName)
            const fieldPlaceholder = getFieldPlaceholder(field)
            const defaultStringValue =
              typeof field.defaultValue === 'string' ? field.defaultValue : undefined
            const fieldError = fieldErrors[fieldName]
            const isPhoneField = field.blockType === 'phone'
            const isEmailField = field.blockType === 'email'

            if (field.blockType === 'message') {
              return (
                <Field key={fieldName}>
                  <p className="text-muted-foreground text-sm">
                    {typeof field.message === 'string' ? field.message : 'Informational message'}
                  </p>
                </Field>
              )
            }

            if (field.blockType === 'textarea') {
              return (
                <Field key={fieldName}>
                  <FieldLabel htmlFor={fieldId}>{fieldLabel}</FieldLabel>
                  <Textarea
                    className="min-h-[106px]"
                    id={fieldId}
                    onChange={(event) => onChangeValue(fieldName, event.target.value)}
                    placeholder={fieldPlaceholder}
                    required={Boolean(field.required)}
                    value={String(values[fieldName] ?? '')}
                  />
                </Field>
              )
            }

            if (field.blockType === 'checkbox') {
              const lexicalCheckboxLabel = isLexicalCheckboxLabel(field.label) ? field.label : null
              const richTextLabelDomId = `${fieldId}-richtext-label`
              const checkedBox = Boolean(values[fieldName] ?? field.defaultValue ?? false)

              return (
                <Field key={fieldName} orientation="horizontal">
                  <Input
                    aria-labelledby={
                      lexicalCheckboxLabel ? richTextLabelDomId : undefined
                    }
                    checked={checkedBox}
                    id={fieldId}
                    onChange={(event) => onChangeValue(fieldName, event.target.checked)}
                    required={Boolean(field.required)}
                    type="checkbox"
                  />
                  {lexicalCheckboxLabel ? (
                    <div
                      id={richTextLabelDomId}
                      className={cn(
                        'payload-rich-text text-muted-foreground inline-flex min-w-0 flex-1 cursor-pointer select-none gap-2 text-sm leading-normal font-normal',
                        '[&_p]:my-0',
                        '[&_a]:font-medium [&_a]:text-brand-gold [&_a]:no-underline [&_a]:transition-colors',
                        '[&_a:hover]:underline [&_a:hover]:underline-offset-2',
                        '[&_a:hover]:decoration-[color-mix(in_srgb,var(--color-brand-gold)_85%,transparent)]',
                        '[&_a:hover]:text-[color-mix(in_srgb,var(--color-brand-gold)_82%,white)]',
                        '[&_a:focus-visible]:underline [&_a:focus-visible]:underline-offset-2',
                        '[&_a:focus-visible]:decoration-[color-mix(in_srgb,var(--color-brand-gold)_85%,transparent)]',
                        '[&_a:focus-visible]:text-[color-mix(in_srgb,var(--color-brand-gold)_82%,white)]',
                        '[&_a:focus-visible]:rounded-sm [&_a]:outline-none',
                        '[&_a]:focus-visible:ring-2 [&_a]:focus-visible:ring-[color-mix(in_srgb,var(--color-brand-gold)_45%,transparent)]',
                        '[&_a]:focus-visible:ring-offset-2 [&_a]:focus-visible:ring-offset-background',
                      )}
                      onClick={(event) => {
                        if (isInteractiveCheckboxLabelClickTarget(event.target)) return
                        onChangeValue(fieldName, !checkedBox)
                      }}
                      onMouseDown={(event) => {
                        if (isInteractiveCheckboxLabelClickTarget(event.target)) return
                        event.preventDefault()
                      }}
                    >
                      <PayloadRichText data={lexicalCheckboxLabel} disableContainer />
                    </div>
                  ) : (
                    <FieldLabel
                      className="text-muted-foreground inline leading-none font-normal"
                      htmlFor={fieldId}
                    >
                      {fieldLabel}
                    </FieldLabel>
                  )}
                </Field>
              )
            }

            if (field.blockType === 'select') {
              return (
                <Field key={fieldName}>
                  <FieldLabel htmlFor={fieldId}>{fieldLabel}</FieldLabel>
                  <select
                    className="border-input bg-background w-full rounded-md border px-3 py-2 text-base"
                    id={fieldId}
                    onChange={(event) => onChangeValue(fieldName, event.target.value)}
                    required={Boolean(field.required)}
                    value={String(values[fieldName] ?? defaultStringValue ?? '')}
                  >
                    <option value="" disabled>
                      Select an option
                    </option>
                    {(field.options ?? []).map((option) => (
                      <option key={option.value} value={option.value}>
                        {option.label}
                      </option>
                    ))}
                  </select>
                </Field>
              )
            }

            if (field.blockType === 'radio') {
              return (
                <Field key={fieldName}>
                  <FieldLabel>{fieldLabel}</FieldLabel>
                  <div className="flex flex-col gap-2">
                    {(field.options ?? []).map((option) => (
                      <label className="inline-flex items-center gap-2 text-sm" key={option.value}>
                        <input
                          checked={String(values[fieldName] ?? defaultStringValue ?? '') === option.value}
                          name={fieldName}
                          onChange={() => onChangeValue(fieldName, option.value)}
                          required={Boolean(field.required)}
                          type="radio"
                          value={option.value}
                        />
                        <span>{option.label}</span>
                      </label>
                    ))}
                  </div>
                </Field>
              )
            }

            if (field.blockType === 'date') {
              return (
                <Field key={fieldName}>
                  <FieldLabel htmlFor={fieldId}>{fieldLabel}</FieldLabel>
                  <Input
                    id={fieldId}
                    onChange={(event) => onChangeValue(fieldName, event.target.value)}
                    required={Boolean(field.required)}
                    type="date"
                    value={String(values[fieldName] ?? defaultStringValue ?? '')}
                  />
                </Field>
              )
            }

            if (field.blockType === 'number' || field.blockType === 'payment') {
              return (
                <Field key={fieldName}>
                  <FieldLabel htmlFor={fieldId}>{fieldLabel}</FieldLabel>
                  <Input
                    id={fieldId}
                    min={field.blockType === 'payment' ? 0 : undefined}
                    onChange={(event) => onChangeValue(fieldName, Number(event.target.value || 0))}
                    required={Boolean(field.required)}
                    step="any"
                    type="number"
                    value={String(values[fieldName] ?? getInitialValue(field))}
                  />
                </Field>
              )
            }

            if (field.blockType === 'country' || field.blockType === 'state') {
              return (
                <Field key={fieldName}>
                  <FieldLabel htmlFor={fieldId}>{fieldLabel}</FieldLabel>
                  <Input
                    id={fieldId}
                    onChange={(event) => onChangeValue(fieldName, event.target.value)}
                    placeholder={fieldPlaceholder}
                    required={Boolean(field.required)}
                    type="text"
                    value={String(values[fieldName] ?? defaultStringValue ?? '')}
                  />
                </Field>
              )
            }

            return (
              <Field key={fieldName}>
                <FieldLabel htmlFor={fieldId}>{fieldLabel}</FieldLabel>
                <Input
                  id={fieldId}
                  aria-invalid={fieldError ? 'true' : undefined}
                  onChange={(event) => onChangeValue(fieldName, event.target.value)}
                  placeholder={fieldPlaceholder}
                  required={Boolean(field.required)}
                  inputMode={isPhoneField ? 'tel' : undefined}
                  pattern={isPhoneField ? PHONE_PATTERN.source : undefined}
                  title={isPhoneField ? 'Please enter a valid phone number' : undefined}
                  type={isPhoneField ? 'tel' : isEmailField ? 'email' : 'text'}
                  value={String(values[fieldName] ?? '')}
                />
                {fieldError ? <p className="text-destructive text-sm">{fieldError}</p> : null}
              </Field>
            )
          })}

          <Field>
            <Button colorMode="gold" className="w-full" disabled={isSubmitting} type="submit">
              {submitLabel}
            </Button>
          </Field>
          {statusMessage ? <p className="text-sm text-muted-foreground">{statusMessage}</p> : null}
        </FieldGroup>
      </FieldSet>
    </form>
  )
}
